import React, { useState, useEffect } from 'react';
import { theme, responsiveContainer, createGradient } from '../styles/theme';

function About() {
  const [animatedStats, setAnimatedStats] = useState({
    businesses: 0,
    uptime: 0,
    support: 0
  });

  // Animate stats on component mount
  useEffect(() => {
    const animateValue = (start, end, duration, key) => {
      const startTime = Date.now();
      const timer = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const current = Math.floor(start + (end - start) * progress);
        
        setAnimatedStats(prev => ({ ...prev, [key]: current }));
        
        if (progress === 1) {
          clearInterval(timer);
        }
      }, 16);
    };

    // Start animations with delays
    setTimeout(() => animateValue(0, 24, 2000, 'uptime'), 500);
    setTimeout(() => animateValue(0, 7, 1500, 'support'), 800);
    setTimeout(() => animateValue(0, 0, 1000, 'businesses'), 1100); // Keep at 0 for honesty
  }, []);

  const styles = {
    container: {
      backgroundColor: theme.colors.background,
      fontFamily: "'Inter', sans-serif",
      minHeight: '100vh',
    },
    
    // Hero Section with animated background
    hero: {
      background: createGradient(theme.colors.primary, theme.colors.primaryDark, '135deg').background,
      color: '#fff',
      padding: '120px 20px 80px 20px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    },
    
    heroPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.1,
      background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Crect x='11' y='11' width='4' height='4'/%3E%3Crect x='51' y='11' width='4' height='4'/%3E%3Crect x='11' y='51' width='4' height='4'/%3E%3Crect x='51' y='51' width='4' height='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    },
    
    heroContent: {
      ...responsiveContainer,
      position: 'relative',
      zIndex: 1,
    },
    
    heroTitle: {
      fontSize: theme.typography.fontSizes['6xl'],
      fontWeight: theme.typography.fontWeights.extrabold,
      marginBottom: theme.spacing.lg,
      lineHeight: '1.1',
      textShadow: '0 2px 4px rgba(0,0,0,0.1)',
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    heroSubtitle: {
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.normal,
      marginBottom: theme.spacing['3xl'],
      maxWidth: '800px',
      margin: `0 auto ${theme.spacing['3xl']} auto`,
      lineHeight: '1.6',
      opacity: 0.95,
    },

    // Animated Stats Section
    statsSection: {
      backgroundColor: theme.colors.secondary,
      color: '#fff',
      padding: '80px 20px',
      position: 'relative',
    },
    
    statsOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: createGradient('rgba(0,122,204,0.1)', 'rgba(26,140,221,0.1)').background,
    },
    
    statsContent: {
      ...responsiveContainer,
      position: 'relative',
      zIndex: 1,
    },
    
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: theme.spacing['2xl'],
      textAlign: 'center',
    },
    
    statItem: {
      padding: theme.spacing.xl,
    },
    
    statNumber: {
      fontSize: theme.typography.fontSizes['5xl'],
      fontWeight: theme.typography.fontWeights.extrabold,
      color: theme.colors.primary,
      marginBottom: theme.spacing.sm,
      textShadow: '0 2px 4px rgba(0,0,0,0.1)',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    statLabel: {
      fontSize: theme.typography.fontSizes.xl,
      fontWeight: theme.typography.fontWeights.medium,
      opacity: 0.9,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },

    // Section Styles
    section: {
      padding: '100px 20px',
    },
    
    sectionContent: {
      ...responsiveContainer,
    },
    
    sectionTitle: {
      fontSize: theme.typography.fontSizes['4xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing['2xl'],
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      position: 'relative',
    },
    
    sectionTitleCentered: {
      textAlign: 'center',
      marginBottom: theme.spacing['3xl'],
    },
    
    sectionTitleUnderline: {
      content: '""',
      position: 'absolute',
      bottom: '-10px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '80px',
      height: '4px',
      backgroundColor: theme.colors.primary,
      borderRadius: theme.borderRadius.full,
    },
    
    content: {
      fontSize: theme.typography.fontSizes.lg,
      color: theme.colors.textLight,
      lineHeight: '1.8',
      marginBottom: theme.spacing.xl,
    },

    // Mission Section with gradient
    mission: {
      background: createGradient(theme.colors.secondary, '#1a1a1a', '45deg').background,
      color: '#fff',
      padding: '120px 20px',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    },
    
    missionContent: {
      ...responsiveContainer,
      position: 'relative',
      zIndex: 1,
    },
    
    missionTitle: {
      fontSize: theme.typography.fontSizes['5xl'],
      fontWeight: theme.typography.fontWeights.bold,
      marginBottom: theme.spacing['2xl'],
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
    },
    
    missionText: {
      fontSize: theme.typography.fontSizes['2xl'],
      lineHeight: '1.6',
      maxWidth: '900px',
      margin: '0 auto',
      opacity: 0.95,
      fontWeight: theme.typography.fontWeights.medium,
    },

    // Values Section with enhanced cards
    values: {
      backgroundColor: theme.colors.surface,
      padding: '120px 20px',
    },
    
    valuesGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
      gap: theme.spacing['3xl'],
      marginTop: theme.spacing['3xl'],
    },
    
    valueCard: {
      backgroundColor: theme.colors.background,
      border: `2px solid ${theme.colors.borderDark}`,
      borderRadius: theme.borderRadius.xl,
      padding: theme.spacing['3xl'],
      textAlign: 'center',
      transition: 'all 0.4s ease',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: theme.shadows.md,
    },
    
    valueCardGlow: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${theme.colors.primary}10, transparent)`,
      opacity: 0,
      transition: 'opacity 0.4s ease',
    },
    
    valueIcon: {
      fontSize: theme.typography.fontSizes['5xl'],
      marginBottom: theme.spacing.xl,
      color: theme.colors.primary,
      fontWeight: theme.typography.fontWeights.bold,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      textShadow: `0 0 20px ${theme.colors.primary}40`,
    },
    
    valueTitle: {
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.lg,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    valueDescription: {
      color: theme.colors.textLight,
      lineHeight: '1.7',
      fontSize: theme.typography.fontSizes.lg,
    },

    // Story Section with alternating layout
    story: {
      backgroundColor: theme.colors.background,
      padding: '120px 20px',
    },
    
    storyGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
      gap: theme.spacing['4xl'],
      alignItems: 'center',
      marginTop: theme.spacing['2xl'],
    },
    
    storyContent: {
      order: 1,
    },
    
    storyVisual: {
      order: 2,
      textAlign: 'center',
    },
    
    highlight: {
      backgroundColor: theme.colors.primary,
      color: '#fff',
      padding: theme.spacing['3xl'],
      borderRadius: theme.borderRadius.xl,
      margin: `${theme.spacing['3xl']} 0`,
      textAlign: 'center',
      border: `2px solid ${theme.colors.primary}`,
      position: 'relative',
      overflow: 'hidden',
      boxShadow: theme.shadows.xl,
    },
    
    highlightPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.1,
      background: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M20 20c0 4.4-3.6 8-8 8s-8-3.6-8-8 3.6-8 8-8 8 3.6 8 8zm0-20c0 4.4-3.6 8-8 8s-8-3.6-8-8 3.6-8 8-8 8 3.6 8 8z'/%3E%3C/g%3E%3C/svg%3E")`,
    },
    
    highlightText: {
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.semibold,
      margin: 0,
      lineHeight: '1.5',
      fontStyle: 'italic',
      position: 'relative',
      zIndex: 1,
    },

    // Why Now Section - Enhanced
    whyNow: {
      background: createGradient('#1a1a1a', theme.colors.secondary, '135deg').background,
      color: '#fff',
      padding: '120px 20px',
      position: 'relative',
      overflow: 'hidden',
    },
    
    whyNowOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23007acc' fill-opacity='0.05'%3E%3Cpath d='M0 0h80v80H0V0zm20 20v40h40V20H20zm20 35a15 15 0 1 1 0-30 15 15 0 0 1 0 30z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      opacity: 0.3,
    },
    
    whyNowGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: theme.spacing['3xl'],
      marginTop: theme.spacing['3xl'],
      marginBottom: theme.spacing['3xl'],
    },
    
    whyNowCard: {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      border: `2px solid rgba(255, 255, 255, 0.2)`,
      borderRadius: theme.borderRadius.xl,
      padding: theme.spacing['2xl'],
      textAlign: 'center',
      transition: 'all 0.4s ease',
      position: 'relative',
      overflow: 'hidden',
      backdropFilter: 'blur(10px)',
    },
    
    whyNowIcon: {
      fontSize: theme.typography.fontSizes['5xl'],
      marginBottom: theme.spacing.lg,
      color: theme.colors.primary,
      fontWeight: theme.typography.fontWeights.bold,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      textShadow: `0 0 20px ${theme.colors.primary}60`,
    },
    
    whyNowCardTitle: {
      fontSize: theme.typography.fontSizes.xl,
      fontWeight: theme.typography.fontWeights.bold,
      color: '#fff',
      marginBottom: theme.spacing.md,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    whyNowCardText: {
      color: 'rgba(255, 255, 255, 0.9)',
      lineHeight: '1.6',
      fontSize: theme.typography.fontSizes.base,
    },
    
    whyNowCta: {
      textAlign: 'center',
      padding: theme.spacing['2xl'],
      backgroundColor: 'rgba(0, 122, 204, 0.2)',
      borderRadius: theme.borderRadius.xl,
      border: `2px solid ${theme.colors.primary}40`,
      backdropFilter: 'blur(10px)',
    },
    
    whyNowCtaText: {
      fontSize: theme.typography.fontSizes.xl,
      fontWeight: theme.typography.fontWeights.medium,
      color: '#fff',
      lineHeight: '1.6',
      margin: 0,
    },

    // Company Section with cards
    company: {
      backgroundColor: theme.colors.surface,
      padding: '120px 20px',
    },
    
    companyGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: theme.spacing['2xl'],
      marginTop: theme.spacing['3xl'],
    },
    
    companyCard: {
      backgroundColor: theme.colors.background,
      border: `2px solid ${theme.colors.borderDark}`,
      borderRadius: theme.borderRadius.xl,
      padding: theme.spacing['2xl'],
      textAlign: 'center',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
    },
    
    companyIcon: {
      fontSize: theme.typography.fontSizes['5xl'],
      color: theme.colors.primary,
      marginBottom: theme.spacing.lg,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      fontWeight: theme.typography.fontWeights.bold,
      textShadow: `0 0 20px ${theme.colors.primary}40`,
    },
    
    companyTitle: {
      fontSize: theme.typography.fontSizes.xl,
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      marginBottom: theme.spacing.md,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    
    companyText: {
      color: theme.colors.textLight,
      lineHeight: '1.6',
      fontSize: theme.typography.fontSizes.base,
    },

    // Visual elements
    terminalBox: {
      backgroundColor: '#1a1a1a',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.xl,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      color: theme.colors.primary,
      fontSize: theme.typography.fontSizes.sm,
      lineHeight: '1.6',
      position: 'relative',
      overflow: 'hidden',
    },
    
    terminalHeader: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: theme.spacing.md,
      paddingBottom: theme.spacing.sm,
      borderBottom: `1px solid ${theme.colors.primary}40`,
    },
    
    terminalDots: {
      display: 'flex',
      gap: theme.spacing.xs,
    },
    
    terminalDot: {
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      backgroundColor: theme.colors.primary,
      opacity: 0.6,
    },
    
    terminalTitle: {
      marginLeft: theme.spacing.md,
      color: '#fff',
      fontSize: theme.typography.fontSizes.sm,
    },

    // Responsive
    '@media (maxWidth: 768px)': {
      heroTitle: {
        fontSize: theme.typography.fontSizes['4xl'],
      },
      valuesGrid: {
        gridTemplateColumns: '1fr',
      },
      companyGrid: {
        gridTemplateColumns: '1fr',
      },
      storyGrid: {
        gridTemplateColumns: '1fr',
      },
      whyNowGrid: {
        gridTemplateColumns: '1fr',
      },
    },
  };

  const handleCardHover = (e, isHovering) => {
    if (isHovering) {
      e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
      e.currentTarget.style.boxShadow = theme.shadows.xl;
      const glow = e.currentTarget.querySelector('.card-glow');
      if (glow) glow.style.opacity = '1';
    } else {
      e.currentTarget.style.transform = 'translateY(0) scale(1)';
      e.currentTarget.style.boxShadow = theme.shadows.md;
      const glow = e.currentTarget.querySelector('.card-glow');
      if (glow) glow.style.opacity = '0';
    }
  };

  return (
    <div style={styles.container}>
      {/* Enhanced Hero Section */}
      <section style={styles.hero}>
        <div style={styles.heroPattern}></div>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>
            About ByteBot
          </h1>
          <p style={styles.heroSubtitle}>
            Making AI chatbots accessible to every business. 
            No coding required, no AI expertise needed. 
            Just results that matter.
          </p>
        </div>
      </section>

      {/* Animated Stats Section */}
      <section style={styles.statsSection}>
        <div style={styles.statsOverlay}></div>
        <div style={styles.statsContent}>
          <div style={styles.statsGrid}>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>[{animatedStats.uptime}]</div>
              <div style={styles.statLabel}>Hours Available</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>[{animatedStats.support}]</div>
              <div style={styles.statLabel}>Days a Week</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statNumber}>[∞]</div>
              <div style={styles.statLabel}>Scalability</div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story with Visual Elements */}
      <section style={styles.story}>
        <div style={styles.sectionContent}>
          <div style={styles.storyGrid}>
            <div style={styles.storyContent}>
              <h2 style={styles.sectionTitle}>Our Story</h2>
              <p style={styles.content}>
                Small businesses are the backbone of the economy, yet they're often left behind 
                when it comes to cutting-edge technology. While enterprise companies have dedicated 
                IT teams and massive budgets for AI implementation, small business owners are stuck 
                choosing between expensive consultants or going without.
              </p>
              <p style={styles.content}>
                ByteBot was created to bridge this gap. We believe that powerful AI chatbot 
                technology should be accessible to any business, regardless of size or technical knowledge.
              </p>
            </div>
            
            <div style={styles.storyVisual}>
              <div style={styles.terminalBox}>
                <div style={styles.terminalHeader}>
                  <div style={styles.terminalDots}>
                    <div style={styles.terminalDot}></div>
                    <div style={styles.terminalDot}></div>
                    <div style={styles.terminalDot}></div>
                  </div>
                  <div style={styles.terminalTitle}>ByteBot Terminal</div>
                </div>
                                 <div>
                   $ initialize_chatbot --business="small"<br/>
                   {"> Analyzing business needs..."}<br/>
                   {"> Building knowledge base..."}<br/>
                   {"> Configuring responses..."}<br/>
                   {"> [SUCCESS] Chatbot deployed!"}<br/>
                   {"> Ready to serve customers 24/7"}
                 </div>
              </div>
            </div>
          </div>
          
          <div style={styles.highlight}>
            <div style={styles.highlightPattern}></div>
            <p style={styles.highlightText}>
              "Small businesses drive the economy, but they're stuck with enterprise pricing for AI. 
              We're changing that – powerful chatbots shouldn't cost more than your rent."
            </p>
          </div>

          <p style={styles.content}>
            We've built ByteBot with simplicity at its core – a platform that creates 
            custom chatbots in minutes, not months. No coding required. No AI expertise needed. 
            Just simple questions about your business and you're ready to go.
          </p>
        </div>
      </section>

      {/* Enhanced Mission */}
      <section style={styles.mission}>
        <div style={styles.missionContent}>
          <h2 style={styles.missionTitle}>Our Mission</h2>
          <p style={styles.missionText}>
            To make enterprise-level AI chatbot technology accessible and affordable 
            for small and medium businesses through simple, no-code solutions.
          </p>
        </div>
      </section>

      {/* Enhanced Values */}
      <section style={styles.values}>
        <div style={styles.sectionContent}>
          <h2 style={{...styles.sectionTitle, ...styles.sectionTitleCentered}}>
            What Drives Us
            <div style={styles.sectionTitleUnderline}></div>
          </h2>
          <div style={styles.valuesGrid}>
            <div 
              style={styles.valueCard}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}
            >
              <div className="card-glow" style={styles.valueCardGlow}></div>
              <div style={styles.valueIcon}>[SIMPLE]</div>
              <h3 style={styles.valueTitle}>Simplicity First</h3>
              <p style={styles.valueDescription}>
                Technology should work for you, not against you. We focus on making 
                complex AI simple enough for anyone to use and implement.
              </p>
            </div>
            <div 
              style={styles.valueCard}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}
            >
              <div className="card-glow" style={styles.valueCardGlow}></div>
              <div style={styles.valueIcon}>[FAST]</div>
              <h3 style={styles.valueTitle}>Instant Value</h3>
              <p style={styles.valueDescription}>
                Every feature we build must deliver immediate, measurable value to your 
                business. No fluff, no complexity – just results that matter.
              </p>
            </div>
            <div 
              style={styles.valueCard}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}
            >
              <div className="card-glow" style={styles.valueCardGlow}></div>
              <div style={styles.valueIcon}>[SMB]</div>
              <h3 style={styles.valueTitle}>Small Business Focus</h3>
              <p style={styles.valueDescription}>
                We understand the unique challenges small businesses face. Our pricing, 
                features, and support are designed specifically for growing companies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Now - Enhanced */}
      <section style={styles.whyNow}>
        <div style={styles.whyNowOverlay}></div>
        <div style={styles.sectionContent}>
          <h2 style={{...styles.sectionTitle, ...styles.sectionTitleCentered, color: '#fff'}}>
            Why This Matters Now
            <div style={{...styles.sectionTitleUnderline, left: '50%'}}></div>
          </h2>
          
          <div style={styles.whyNowGrid}>
            <div 
              style={styles.whyNowCard}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}
            >
              <div style={styles.whyNowIcon}>[24/7]</div>
              <h3 style={styles.whyNowCardTitle}>Customer Expectations</h3>
              <p style={styles.whyNowCardText}>
                Today's consumers expect instant, 24/7 support across all channels. 
                They want their questions answered immediately, whether it's 2 PM or 2 AM.
              </p>
            </div>
            
            <div 
              style={styles.whyNowCard}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}
            >
              <div style={styles.whyNowIcon}>[COST]</div>
              <h3 style={styles.whyNowCardTitle}>Rising Labor Costs</h3>
              <p style={styles.whyNowCardText}>
                Hiring and retaining customer service staff has become increasingly 
                challenging and expensive. Small businesses need consistent support without the overhead.
              </p>
            </div>
            
            <div 
              style={styles.whyNowCard}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}
            >
              <div style={styles.whyNowIcon}>[NOW]</div>
              <h3 style={styles.whyNowCardTitle}>AI is Here</h3>
              <p style={styles.whyNowCardText}>
                AI chatbots aren't just the future – they're the present. The question isn't 
                whether your business needs one, but how quickly you can implement one.
              </p>
            </div>
          </div>
          
          <div style={styles.whyNowCta}>
            <p style={styles.whyNowCtaText}>
              ByteBot removes every barrier between you and professional AI customer support. 
              No technical teams. No lengthy development cycles. No massive upfront costs. Just results.
            </p>
          </div>
        </div>
      </section>

      {/* Built by ByteSizeDS */}
      <section style={styles.company}>
        <div style={styles.sectionContent}>
          <h2 style={{...styles.sectionTitle, ...styles.sectionTitleCentered}}>
            Built by ByteSizeDS
            <div style={styles.sectionTitleUnderline}></div>
          </h2>
          <div style={styles.companyGrid}>
            <div 
              style={styles.companyCard}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}
            >
              <div style={styles.companyIcon}>[WEB]</div>
              <h3 style={styles.companyTitle}>Digital Agency</h3>
              <p style={styles.companyText}>
                ByteBot is developed by ByteSizeDS, a digital agency 
                specializing in custom websites and automations for small to medium businesses.
              </p>
            </div>
            <div 
              style={styles.companyCard}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}
            >
              <div style={styles.companyIcon}>[TECH]</div>
              <h3 style={styles.companyTitle}>Tech Solutions</h3>
              <p style={styles.companyText}>
                We build custom solutions that eliminate manual tasks and streamline 
                workflows, helping businesses focus on what matters most.
              </p>
            </div>
            <div 
              style={styles.companyCard}
              onMouseEnter={(e) => handleCardHover(e, true)}
              onMouseLeave={(e) => handleCardHover(e, false)}
            >
              <div style={styles.companyIcon}>[HONEST]</div>
              <h3 style={styles.companyTitle}>Commitment</h3>
              <p style={styles.companyText}>
                We're committed to honest pricing, transparent features, 
                and building tools that actually solve real business problems.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About; 